import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, Check, Loader2 } from "lucide-react";

/**
 * ImageCropDialog — modale de recadrage carré/paysage pour l'admin
 * avant upload de l'image d'un popup.
 *
 * Le popup affiche l'image en `w-full max-h-[340px] object-cover` dans
 * une modale ~416px de large, soit un ratio ≈ 4:3. On propose donc le
 * ratio 4:3 par défaut, avec possibilité de basculer sur 1:1 ou 16:9.
 *
 * Props :
 *  - open       : bool
 *  - fileUrl    : dataURL de l'image source (créé par URL.createObjectURL)
 *  - onCancel() : fermeture sans upload
 *  - onConfirm(blob, filename) : reçoit le blob JPEG recadré prêt à uploader
 */
const RATIOS = [
  { v: 4 / 3, label: "4:3" },
  { v: 1, label: "Carré" },
  { v: 16 / 9, label: "16:9" },
];

async function getCroppedBlob(imageSrc, cropPixels, maxWidth = 1600) {
  // Charge l'image source
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });

  // Applique le crop à la vraie résolution puis redimensionne si trop grand
  const scale = cropPixels.width > maxWidth ? maxWidth / cropPixels.width : 1;
  const outW = Math.round(cropPixels.width * scale);
  const outH = Math.round(cropPixels.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, outW, outH,
  );

  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.88);
  });
}

export default function ImageCropDialog({ open, fileUrl, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4 / 3);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixels(areaPixels);
  }, []);

  if (!open) return null;

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(fileUrl, pixels);
      if (!blob) throw new Error("crop failed");
      onConfirm(blob, `popup-${Date.now()}.jpg`);
    } catch (_) {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm px-3"
      data-testid="image-crop-dialog"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full sm:max-w-md bg-[#FFF8F1] rounded-3xl border-2 border-black shadow-[0_8px_0_0_#000] flex flex-col overflow-hidden max-h-[92dvh]">
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-black bg-white">
          <div className="font-[Outfit] text-[15px] font-black">Recadrer l&apos;image</div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="image-crop-cancel"
            aria-label="Fermer"
            className="w-9 h-9 rounded-xl border-2 border-black grid place-items-center bg-white active:translate-y-[2px] transition-transform"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        <div className="relative w-full h-[55vh] max-h-[420px] bg-neutral-900">
          <Cropper
            image={fileUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </div>

        <div className="px-4 py-3 space-y-3 border-t-2 border-black bg-[#FFF8F1]">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mb-1.5">
              Ratio
            </div>
            <div className="grid grid-cols-3 gap-2">
              {RATIOS.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => setAspect(r.v)}
                  data-testid={`image-crop-ratio-${r.label}`}
                  className={
                    "street-press rounded-xl py-2 text-[11px] font-black uppercase tracking-widest border-2 border-black " +
                    (aspect === r.v
                      ? "bg-primary text-primary-foreground shadow-[0_3px_0_0_#000]"
                      : "bg-white text-foreground shadow-[0_2px_0_0_#000]")
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
                Zoom
              </div>
              <div className="text-[10px] font-black text-foreground/60">
                {zoom.toFixed(1)}x
              </div>
            </div>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="image-crop-zoom"
              className="w-full accent-primary"
            />
          </div>
        </div>

        <div className="border-t-2 border-black bg-white px-3 py-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="image-crop-cancel-btn"
            className="flex-1 h-11 rounded-xl bg-white text-black font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy || !pixels}
            data-testid="image-crop-confirm-btn"
            className="flex-[1.4] h-11 rounded-xl bg-primary text-primary-foreground font-[Outfit] font-black text-[13px] uppercase tracking-wider border-2 border-black shadow-[0_3px_0_0_#000] active:translate-y-[2px] active:shadow-[0_1px_0_0_#000] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
                Traitement…
              </>
            ) : (
              <>
                <Check className="w-4 h-4" strokeWidth={3} />
                Utiliser cette image
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
